/**
 * SchemaImportModal — FR-001 partner-schema importer review modal.
 *
 * Three-region layout:
 *   Region 1 — source select + paste textarea + Parse button
 *   Region 2 — parsed field catalog table (checkbox, name, type, required, order)
 *   Region 3 — conformance policy switches (5 booleans + Hard/Soft level)
 *
 * Produces a SchemaImportResult: { columns, contract | null, rawSchemaText } via onApply.
 * If hasExistingColumns, first shows Replace/Append/Cancel choice.
 */

import React, { useEffect, useRef, useState, type ReactElement } from 'react';
import {
  type ImportSource,
  type ImportedSchema,
  type ImportedField,
  type ConformancePolicy,
  type ConformanceContract,
  type ColumnFormatSpec,
  type ExportFormatStructureNode,
} from '@capture/shared';
import { type FormatColumn } from './FormatColumnsEditor';
import { api } from '@/lib/api-client';
import styles from './SchemaImportModal.module.css';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface SchemaImportResult {
  columns: FormatColumn[];
  contract: ConformanceContract | null;
  /**
   * The parsed structure tree, passed through whole (Slice 3b). Callers on the
   * tree-editor path (xml/json) apply this; the flat columns path ignores it.
   * In-modal prune/retype of the tree is a deferred follow-up — see the slice
   * plan's Carry-forward.
   */
  structure: ExportFormatStructureNode[];
  /**
   * Task 5 (Partner Profile Completion) — the raw pasted schema text, carried
   * through as provenance. Callers persist this into the format's
   * `schemaAttachment` field so the ORIGINAL partner schema is recoverable
   * later, independent of whether any conformance policy was switched on.
   * Pasting/importing here does NOT itself enforce anything — enforcement is
   * solely the `contract` field.
   */
  rawSchemaText: string;
}

export interface SchemaImportModalProps {
  open: boolean;
  onClose: () => void;
  onApply: (result: SchemaImportResult, mode: 'replace' | 'append') => void;
  hasExistingColumns: boolean;
  initialContent?: string;
  initialSource?: ImportSource;
  /**
   * What the consumer applies. 'columns' (default) gates Apply on ≥1 checked
   * flat row (the flat columns editor). 'structure' gates Apply on a parsed
   * structure tree being present (the xml/json tree editor) — flat-row
   * selection is irrelevant there.
   */
  applyTarget?: 'columns' | 'structure';
}

// ─── Local EMPTY_SPEC mirror ──────────────────────────────────────────────────
// We intentionally do NOT import EMPTY_SPEC from FormatColumnsEditor (it is not
// exported). Define the same 4-entry map here per task constraints.

const EMPTY_SPEC: Record<ColumnFormatSpec['kind'], ColumnFormatSpec> = {
  raw: { kind: 'raw' },
  text: { kind: 'text' },
  date: { kind: 'date', token: 'YYYY-MM-DD' },
  number: { kind: 'number' },
};

// ─── Pure tree helpers ────────────────────────────────────────────────────────
// Path scheme mirrors flattenStructureLeaves: [...prefix, node.name].join("/")

/**
 * Remove the node at `targetPath` (and its entire subtree) from the tree.
 * Returns a new tree array; original is not mutated.
 */
export function pruneNodeAt(
  tree: ExportFormatStructureNode[],
  targetPath: string,
  prefix: string[] = [],
): ExportFormatStructureNode[] {
  const result: ExportFormatStructureNode[] = [];
  for (const node of tree) {
    const path = [...prefix, node.name].join('/');
    if (path === targetPath) {
      // Drop this node (and by implication its subtree)
      continue;
    }
    if (node.children && node.children.length > 0) {
      const prunedChildren = pruneNodeAt(node.children, targetPath, [...prefix, node.name]);
      result.push({ ...node, children: prunedChildren });
    } else {
      result.push(node);
    }
  }
  return result;
}

/**
 * Update the dataType (and default format) of the field node at `targetPath`.
 * Returns a new tree; original is not mutated.
 */
export function retypeNodeAt(
  tree: ExportFormatStructureNode[],
  targetPath: string,
  dataType: ColumnFormatSpec['kind'],
  prefix: string[] = [],
): ExportFormatStructureNode[] {
  return tree.map((node) => {
    const path = [...prefix, node.name].join('/');
    if (path === targetPath && node.type === 'field') {
      return { ...node, dataType, format: EMPTY_SPEC[dataType] };
    }
    if (node.children && node.children.length > 0) {
      return {
        ...node,
        children: retypeNodeAt(node.children, targetPath, dataType, [...prefix, node.name]),
      };
    }
    return node;
  });
}

// Build a column-note receiver hint from the parsed field's source type +
// captured constraints (maxLength / pattern). Returns undefined when empty.
function noteHint(field: ImportedField): string | undefined {
  const parts = [
    field.sourceType,
    field.constraints?.maxLength != null ? `maxLength ${field.constraints.maxLength}` : undefined,
    field.constraints?.pattern ? `pattern ${field.constraints.pattern}` : undefined,
  ].filter(Boolean);
  return parts.length > 0 ? parts.join(' · ') : undefined;
}

// ─── Source labels ────────────────────────────────────────────────────────────

const SOURCE_OPTIONS: { value: ImportSource; label: string }[] = [
  { value: 'xsd', label: 'XSD Schema' },
  { value: 'json_schema', label: 'JSON Schema' },
  { value: 'sample_xml', label: 'Sample XML' },
  { value: 'sample_csv', label: 'Sample CSV' },
  { value: 'csv_header', label: 'CSV Header Row' },
];

// ─── Editable structure preview ──────────────────────────────────────────────
// Renders the proposed structure tree with per-node prune + per-field retype
// controls. Preserves data-testid="structure-preview-node" on every node row
// so existing SchemaImportModal.structure.test.tsx assertions still pass.

interface EditableStructurePreviewProps {
  nodes: ExportFormatStructureNode[];
  onPrune: (path: string) => void;
  onRetype: (path: string, dataType: ColumnFormatSpec['kind']) => void;
  classes: { node?: string; name?: string; badge?: string };
  depth?: number;
  prefix?: string[];
}

function EditableStructurePreview({
  nodes,
  onPrune,
  onRetype,
  classes,
  depth = 0,
  prefix = [],
}: EditableStructurePreviewProps): React.ReactElement {
  return (
    <>
      {nodes.map((node, i) => {
        const path = [...prefix, node.name].join('/');
        const isField = node.type === 'field';
        return (
          <div key={`${path}-${i}`}>
            <div
              className={classes.node}
              style={{ marginLeft: depth * 16, display: 'flex', alignItems: 'center', gap: 4 }}
              data-testid="structure-preview-node"
            >
              <span className={classes.name}>{node.name}</span>
              <span className={classes.badge}>
                {isField ? (node.dataType ?? 'raw') : node.type}
              </span>
              {isField && (
                <select
                  aria-label={`Data type for ${node.name}`}
                  value={node.dataType ?? 'raw'}
                  onChange={(e) => onRetype(path, e.target.value as ColumnFormatSpec['kind'])}
                >
                  <option value="raw">Raw</option>
                  <option value="text">Text</option>
                  <option value="date">Date</option>
                  <option value="number">Number</option>
                </select>
              )}
              <button
                type="button"
                aria-label={`Delete ${node.name}`}
                onClick={() => onPrune(path)}
              >
                ×
              </button>
            </div>
            {node.children && node.children.length > 0 && (
              <EditableStructurePreview
                nodes={node.children}
                onPrune={onPrune}
                onRetype={onRetype}
                classes={classes}
                depth={depth + 1}
                prefix={[...prefix, node.name]}
              />
            )}
          </div>
        );
      })}
    </>
  );
}

// ─── Row state ────────────────────────────────────────────────────────────────

interface FieldRow {
  field: ImportedField;
  checked: boolean;
  dataType: ColumnFormatSpec['kind'];
}

// ─── Component ────────────────────────────────────────────────────────────────

export function SchemaImportModal({
  open,
  onClose,
  onApply,
  hasExistingColumns,
  initialContent = '',
  initialSource = 'csv_header',
  applyTarget = 'columns',
}: SchemaImportModalProps): ReactElement | null {
  const [source, setSource] = useState<ImportSource>(initialSource);
  const [content, setContent] = useState<string>(initialContent);
  const [parsing, setParsing] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);
  const [schema, setSchema] = useState<ImportedSchema | null>(null);
  const [rows, setRows] = useState<FieldRow[]>([]);
  // Modal-local editable copy of the parsed structure tree (prune/retype)
  const [editedStructure, setEditedStructure] = useState<ExportFormatStructureNode[]>([]);

  // Policy
  const [requireRequiredFields, setRequireRequiredFields] = useState(false);
  const [enforceOrder, setEnforceOrder] = useState(false);
  const [enforceTypes, setEnforceTypes] = useState(false);
  const [enforceConstraints, setEnforceConstraints] = useState(false);
  const [closedSet, setClosedSet] = useState(false);
  const [level, setLevel] = useState<'hard' | 'soft'>('soft');

  // Replace/append confirmation step
  const [showModeChoice, setShowModeChoice] = useState(false);

  const panelRef = useRef<HTMLDivElement>(null);

  // Escape key handler
  useEffect(() => {
    if (!open) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [open, onClose]);

  // Reset parse state when modal is re-opened
  useEffect(() => {
    if (open) {
      setSource(initialSource);
      setContent(initialContent);
      setParsing(false);
      setParseError(null);
      setSchema(null);
      setRows([]);
      setEditedStructure([]);
      setRequireRequiredFields(false);
      setEnforceOrder(false);
      setEnforceTypes(false);
      setEnforceConstraints(false);
      setClosedSet(false);
      setLevel('soft');
      setShowModeChoice(false);
    }
  }, [open, initialSource, initialContent]);

  if (!open) return null;

  // ── Helpers ──────────────────────────────────────────────────────────────

  async function handleParse() {
    setParseError(null);
    setParsing(true);
    try {
      const { schema: parsed } = await api.post<{ schema: ImportedSchema }>(
        '/export-formats/parse-schema',
        { source, content },
      );
      setSchema(parsed);
      const newRows: FieldRow[] = parsed.fields.map((f) => ({
        field: f,
        checked: f.required,
        dataType: f.type,
      }));
      setRows(newRows);
      // Initialize editable structure from parsed result
      setEditedStructure(parsed.structure ?? []);
      // Auto-set requireRequiredFields ON if any field is required
      const anyRequired = parsed.fields.some((f) => f.required);
      setRequireRequiredFields(anyRequired);
    } catch (err) {
      setParseError(err instanceof Error ? err.message : 'Parse failed');
    } finally {
      setParsing(false);
    }
  }

  function toggleRow(index: number) {
    setRows((prev) => {
      const next = [...prev];
      next[index] = { ...next[index]!, checked: !next[index]!.checked };
      return next;
    });
  }

  function changeRowType(index: number, kind: ColumnFormatSpec['kind']) {
    setRows((prev) => {
      const next = [...prev];
      next[index] = { ...next[index]!, dataType: kind };
      return next;
    });
  }

  function buildResult(): SchemaImportResult {
    const checked = rows.filter((r) => r.checked);
    const columns: FormatColumn[] = checked.map((r, i) => ({
      name: r.field.name,
      order: i + 1,
      dataType: r.dataType,
      format: EMPTY_SPEC[r.dataType],
      // Carry the schema's source type + captured constraints into the column
      // note as a receiver hint (the constraints also live on the contract).
      note: noteHint(r.field),
    }));

    const anyPolicyOn =
      requireRequiredFields ||
      enforceOrder ||
      enforceTypes ||
      enforceConstraints ||
      closedSet;

    const policy: ConformancePolicy = {
      requireRequiredFields,
      enforceOrder,
      enforceTypes,
      enforceConstraints,
      closedSet,
      level,
    };

    const contract: ConformanceContract | null =
      anyPolicyOn && schema
        ? {
            source: schema.source,
            importedAt: new Date().toISOString(),
            fields: schema.fields,
            policy,
          }
        : null;

    return { columns, contract, structure: editedStructure, rawSchemaText: content };
  }

  function handleApply() {
    if (hasExistingColumns) {
      setShowModeChoice(true);
    } else {
      onApply(buildResult(), 'replace');
    }
  }

  function confirmMode(mode: 'replace' | 'append') {
    setShowModeChoice(false);
    onApply(buildResult(), mode);
  }

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div
      className={styles.overlay}
      onClick={onClose}
      data-testid="schema-import-overlay"
    >
      <div
        ref={panelRef}
        className={styles.panel}
        role="dialog"
        aria-label="Import partner schema"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className={styles.title}>Import Partner Schema</h2>

        {/* ── Region 1: Source + Content ── */}
        <section className={styles.region}>
          <div className={styles.row}>
            <label htmlFor="schema-source" className={styles.label}>
              Source
            </label>
            <select
              id="schema-source"
              aria-label="Source"
              className={styles.select}
              value={source}
              onChange={(e) => setSource(e.target.value as ImportSource)}
            >
              {SOURCE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>

          <div className={styles.field}>
            <label htmlFor="schema-content" className={styles.label}>
              Schema Content
            </label>
            <textarea
              id="schema-content"
              aria-label="Schema content"
              className={styles.textarea}
              value={content}
              onChange={(e) => setContent(e.target.value)}
              placeholder="Paste schema text here…"
              rows={6}
            />
          </div>

          {parseError && (
            <div className={styles.error} role="alert">
              {parseError}
            </div>
          )}

          <button
            type="button"
            className={styles.parseBtn}
            onClick={handleParse}
            disabled={parsing || !content.trim()}
          >
            {parsing ? 'Parsing…' : 'Parse'}
          </button>
        </section>

        {/* ── Region 2: Field Catalog ── */}
        {rows.length > 0 && (
          <section className={styles.region}>
            <h3 className={styles.sectionTitle}>Parsed Fields</h3>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th style={{ width: 36 }}></th>
                  <th>Name</th>
                  <th>Type</th>
                  <th style={{ width: 80 }}>Required</th>
                  <th style={{ width: 56 }}>Order</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => (
                  <tr key={r.field.name} className={r.checked ? styles.rowChecked : undefined}>
                    <td>
                      <input
                        type="checkbox"
                        aria-label={`Select field ${r.field.name}`}
                        checked={r.checked}
                        onChange={() => toggleRow(i)}
                      />
                    </td>
                    <td className={styles.fieldName}>{r.field.name}</td>
                    <td>
                      <select
                        aria-label={`Type for ${r.field.name}`}
                        className={styles.typeSelect}
                        value={r.dataType}
                        onChange={(e) => changeRowType(i, e.target.value as ColumnFormatSpec['kind'])}
                      >
                        <option value="raw">Raw</option>
                        <option value="text">Text</option>
                        <option value="date">Date</option>
                        <option value="number">Number</option>
                      </select>
                    </td>
                    <td>
                      {r.field.required && (
                        <span className={styles.requiredBadge}>Required</span>
                      )}
                    </td>
                    <td className={styles.orderCell}>{r.field.order}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}

        {/* ── Proposed Structure preview (editable: prune + retype) ── */}
        {editedStructure.length > 0 && (
          <section className={styles.region}>
            <h3 className={styles.sectionTitle}>Proposed Structure</h3>
            <div className={styles.previewTree}>
              <EditableStructurePreview
                nodes={editedStructure}
                onPrune={(path) => setEditedStructure((prev) => pruneNodeAt(prev, path))}
                onRetype={(path, dataType) =>
                  setEditedStructure((prev) => retypeNodeAt(prev, path, dataType))
                }
                classes={{
                  node: styles.previewNode,
                  name: styles.previewName,
                  badge: styles.previewBadge,
                }}
              />
            </div>
          </section>
        )}

        {/* ── Region 3: Conformance Policy ── */}
        {schema && (
          <section className={styles.region}>
            <h3 className={styles.sectionTitle}>Conformance Policy</h3>
            <div className={styles.policyGrid}>
              <label className={styles.policyRow}>
                <input
                  type="checkbox"
                  aria-label="Require required fields"
                  checked={requireRequiredFields}
                  onChange={(e) => setRequireRequiredFields(e.target.checked)}
                />
                <span>Require required fields</span>
              </label>
              <label className={styles.policyRow}>
                <input
                  type="checkbox"
                  aria-label="Enforce order"
                  checked={enforceOrder}
                  onChange={(e) => setEnforceOrder(e.target.checked)}
                />
                <span>Enforce order</span>
              </label>
              <label className={styles.policyRow}>
                <input
                  type="checkbox"
                  aria-label="Enforce types"
                  checked={enforceTypes}
                  onChange={(e) => setEnforceTypes(e.target.checked)}
                />
                <span>Enforce types</span>
              </label>
              <label className={styles.policyRow}>
                <input
                  type="checkbox"
                  aria-label="Enforce constraints"
                  checked={enforceConstraints}
                  onChange={(e) => setEnforceConstraints(e.target.checked)}
                />
                <span>Enforce constraints</span>
              </label>
              <label className={styles.policyRow}>
                <input
                  type="checkbox"
                  aria-label="Closed set"
                  checked={closedSet}
                  onChange={(e) => setClosedSet(e.target.checked)}
                />
                <span>Closed set (no extra columns)</span>
              </label>
            </div>

            <div className={styles.row} style={{ marginTop: 'var(--space-3)' }}>
              <label htmlFor="policy-level" className={styles.label}>
                Level
              </label>
              <select
                id="policy-level"
                aria-label="Policy level"
                className={styles.select}
                value={level}
                onChange={(e) => setLevel(e.target.value as 'hard' | 'soft')}
              >
                <option value="soft">Soft (warn)</option>
                <option value="hard">Hard (block)</option>
              </select>
            </div>
          </section>
        )}

        {/* ── Replace/Append choice overlay ── */}
        {showModeChoice && (
          <div className={styles.modeChoice}>
            <p className={styles.modeQuestion}>
              This format already has columns. How should the imported columns be applied?
            </p>
            <div className={styles.modeActions}>
              <button
                type="button"
                className={styles.modeBtn}
                onClick={() => setShowModeChoice(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className={styles.modeBtn}
                onClick={() => confirmMode('append')}
              >
                Append
              </button>
              <button
                type="button"
                className={`${styles.modeBtn} ${styles.modeBtnPrimary}`}
                onClick={() => confirmMode('replace')}
              >
                Replace
              </button>
            </div>
          </div>
        )}

        {/* ── Footer actions ── */}
        <div className={styles.actions}>
          <button type="button" className={styles.cancelBtn} onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className={styles.applyBtn}
            onClick={handleApply}
            disabled={
              applyTarget === 'structure'
                ? editedStructure.length === 0
                : rows.filter((r) => r.checked).length === 0
            }
          >
            Apply
          </button>
        </div>
      </div>
    </div>
  );
}
